import { DIAMOND_TYPES, CUSTOM_PRICE_TYPES, DEFAULT_CURRENCY, ORDER_MODES } from '../constants/appConstants';
import { ROLES } from '../constants/roles';
import { hasPermission } from './permissions';
import { roundCarat2dp } from './orderItemDisplay';
import { calculateClientPrice } from './pricing';
import { resolvePricingRules } from './pricingRules';

/** Resolve display/settlement currency for a cart line (matches order email grouping). */
export function getCartItemCurrency(item) {
  if (item?.type === DIAMOND_TYPES.CUSTOM && item.currency) {
    return item.currency;
  }
  if (item?.type === DIAMOND_TYPES.CERTIFIED && item.diamond?.currency) {
    return item.diamond.currency;
  }
  if (item?.type === DIAMOND_TYPES.NON_CERTIFIED && item.lot?.currency) {
    return item.lot.currency;
  }
  return DEFAULT_CURRENCY;
}

/** Manager or employee with edit_order_item_price may override line prices in the cart. */
export function canEditCartItemPrice({ isInternal, userRole, customPermissions }) {
  return isInternal && (
    userRole === ROLES.MANAGER || hasPermission(userRole, 'edit_order_item_price', customPermissions)
  );
}

export function isCartInventoryPriceEditable(item) {
  return item?.type === DIAMOND_TYPES.CERTIFIED || item?.type === DIAMOND_TYPES.NON_CERTIFIED;
}

/** Certified, lots, and custom by-weight lines may have staff price overrides in the cart. */
export function isCartStaffPriceEditable(item) {
  if (isCartInventoryPriceEditable(item)) return true;
  return item?.type === DIAMOND_TYPES.CUSTOM && item?.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT;
}

export function canEditCartItemWeight({ isInternal, userRole, customPermissions }) {
  return isInternal && (
    userRole === ROLES.MANAGER || hasPermission(userRole, 'edit_order_item_weight', customPermissions)
  );
}

export function isCartWeightEditable(item) {
  if (item?.type === DIAMOND_TYPES.NON_CERTIFIED) return true;
  return item?.type === DIAMOND_TYPES.CUSTOM && item?.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT;
}

/** Catalog default price per carat (before staff override). Applies client pricingRule to certified lines. */
export function getCartItemDefaultPricePerUnit(item, clientUser = null) {
  if (item?.type === DIAMOND_TYPES.CERTIFIED) {
    const basePrice = item.diamond?.price;
    if (basePrice == null) return null;
    const numericBase = Number(basePrice);
    if (Number.isNaN(numericBase)) return null;
    const carat = Number(item.diamond?.carat) || 0;
    if (clientUser) {
      const rules = resolvePricingRules(clientUser);
      return calculateClientPrice(numericBase, carat, { pricingRule: rules.certified });
    }
    return numericBase;
  }
  if (item?.type === DIAMOND_TYPES.NON_CERTIFIED) {
    const p = item.lot?.pricePerCarat;
    if (p == null) return null;
    const numericBase = Number(p);
    if (Number.isNaN(numericBase)) return null;
    if (clientUser) {
      const rules = resolvePricingRules(clientUser);
      const weight = Number(item.lot?.caratPerStone) > 0
        ? Number(item.lot.caratPerStone)
        : (Number(item.weight) || 1);
      return calculateClientPrice(numericBase, weight, { pricingRule: rules.lots });
    }
    return numericBase;
  }
  if (item?.type === DIAMOND_TYPES.CUSTOM && item?.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT) {
    const p = item.pricePerUnit;
    if (p == null || p === '') return null;
    const n = Number(p);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/** Effective price per carat used for cart totals and checkout. */
export function getCartItemEffectivePricePerUnit(item, clientUser = null) {
  if (item?.pricePerUnit != null && item.pricePerUnit !== '') {
    const n = Number(item.pricePerUnit);
    if (!Number.isNaN(n)) return n;
  }
  return getCartItemDefaultPricePerUnit(item, clientUser);
}

function getCartItemLineTotal(item, clientUser = null) {
  if (item?.type === DIAMOND_TYPES.CUSTOM) {
    if (item?.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT) {
      const ppu = getCartItemEffectivePricePerUnit(item, clientUser);
      return (Number(item.weight) || 0) * (ppu || 0);
    }
    return Number(item.finalPrice) || 0;
  }
  if (item?.type === DIAMOND_TYPES.CERTIFIED) {
    const ppu = getCartItemEffectivePricePerUnit(item, clientUser);
    return (ppu || 0) * (item.diamond?.carat || 0);
  }
  const ppu = getCartItemEffectivePricePerUnit(item, clientUser);
  return (item.weight || 0) * (ppu || 0);
}

/** Sum cart line totals grouped by item currency (same grouping as order emails). */
export function computeCartTotalsByCurrency(cartItems, clientUser = null) {
  const byCurrency = new Map();
  for (const item of cartItems || []) {
    const currency = getCartItemCurrency(item);
    const amount = getCartItemLineTotal(item, clientUser);
    byCurrency.set(currency, (byCurrency.get(currency) || 0) + amount);
  }
  return [...byCurrency.entries()]
    .map(([currency, total]) => ({ currency, total: Math.round(total * 100) / 100 }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

function hasStaffPriceOverride(item, clientUser = null) {
  if (item?.pricePerUnit == null || item.pricePerUnit === '') return false;
  if (!isCartStaffPriceEditable(item)) return false;
  const override = Number(item.pricePerUnit);
  if (Number.isNaN(override)) return false;
  const defaultPpu = getCartItemDefaultPricePerUnit(item, clientUser);
  if (defaultPpu != null && override === defaultPpu) return false;
  return true;
}

/** Line total for a single cart item (unrounded; computeCartTotalsByCurrency rounds). */
export function getCartItemDisplayTotal(item, clientUser = null) {
  return getCartItemLineTotal(item, clientUser);
}

/**
 * Build POST /api/orders/checkout body from cart line items.
 * pricePerUnit is included only when the caller may edit prices and the line has an override.
 */
export function buildCheckoutPayload(cartItems, {
  canPlaceForClient,
  selectedClient,
  clientNote,
  canEditCartItemPrice: canEditPrice = false,
  isClient = false,
} = {}) {
  const appendItemNotes = (lineItem, item) => {
    if (isClient) {
      if (item.clientItemNote && String(item.clientItemNote).trim()) {
        lineItem.clientItemNote = String(item.clientItemNote).trim();
      }
    } else if (item.staffNote && String(item.staffNote).trim()) {
      lineItem.staffNote = String(item.staffNote).trim();
    }
  };

  const items = (cartItems || []).map((item) => {
    if (item.type === DIAMOND_TYPES.CERTIFIED) {
      const certItem = { type: DIAMOND_TYPES.CERTIFIED, diamondId: item.diamond._id };
      if (canEditPrice && hasStaffPriceOverride(item, selectedClient)) {
        certItem.pricePerUnit = Number(item.pricePerUnit);
      }
      appendItemNotes(certItem, item);
      return certItem;
    }
    if (item.type === DIAMOND_TYPES.CUSTOM) {
      const customItem = {
        type: DIAMOND_TYPES.CUSTOM,
        description: item.description,
        priceType: item.priceType,
      };
      if (item.currency) customItem.currency = item.currency;
      if (item.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT) {
        customItem.weightUnit = item.weightUnit;
        customItem.weight = item.weight;
        const effectivePpu = getCartItemEffectivePricePerUnit(item, selectedClient);
        if (effectivePpu != null) customItem.pricePerUnit = effectivePpu;
      } else {
        customItem.finalPrice = item.finalPrice;
      }
      appendItemNotes(customItem, item);
      return customItem;
    }
    const nonCertItem = {
      type: DIAMOND_TYPES.NON_CERTIFIED,
      lotId: item.lot._id,
      weight: roundCarat2dp(item.weight),
    };
    if (item.orderMode === ORDER_MODES.BY_COUNT) {
      nonCertItem.stoneCount = item.stoneCount;
      nonCertItem.orderMode = ORDER_MODES.BY_COUNT;
    }
    if (canEditPrice && hasStaffPriceOverride(item, selectedClient)) {
      nonCertItem.pricePerUnit = Number(item.pricePerUnit);
    }
    appendItemNotes(nonCertItem, item);
    return nonCertItem;
  });
  const payload = { items };
  if (canPlaceForClient && selectedClient) {
    payload.clientId = selectedClient._id;
  }
  if (clientNote && clientNote.trim()) {
    payload.clientNote = clientNote.trim();
  }
  return payload;
}

export function canShowStaffDeliverActions({ isInternal, userRole, customPermissions }) {
  const canPlaceForClient = isInternal
    && (userRole === ROLES.MANAGER || hasPermission(userRole, 'reserve_for_client', customPermissions));
  const canChangeStatus = isInternal && hasPermission(userRole, 'change_status', customPermissions);
  return canPlaceForClient && canChangeStatus;
}

export function canShowStaffCheckoutToCheck({ isInternal, userRole, customPermissions }) {
  return canShowStaffDeliverActions({ isInternal, userRole, customPermissions })
    && hasPermission(userRole, 'manage_checks', customPermissions);
}

/** Cart line indices that failed during a partial checkout (keep these in the cart). */
export function getFailedCartIndices(itemErrors) {
  return (itemErrors || []).map((entry) => entry.index);
}

/**
 * Orchestrates staff cart checkout: optional group deliver + optional check creation.
 * @param {object} http - axios-like client with post/patch
 */
export async function executeStaffCheckoutFlow(http, {
  payload,
  markDelivered = false,
  createCheck = false,
  clientUserId,
}) {
  const checkoutPayload = markDelivered ? { ...payload, markDelivered: true } : payload;
  const { data } = await http.post('/api/orders/checkout', checkoutPayload);
  const { order, items, itemErrors } = data;

  if (!order?._id) {
    return { order, items, checkId: null, itemErrors };
  }

  let checkId = null;
  if (createCheck && clientUserId && items?.length > 0) {
    try {
      const { data: check } = await http.post('/api/checks', { clientUser: clientUserId });
      checkId = check._id;
      await http.post(`/api/checks/${checkId}/items`, {
        itemIds: items.map((i) => i._id),
      });
    } catch (checkErr) {
      if (checkId) {
        try {
          await http.delete(`/api/checks/${checkId}`);
        } catch {
          // Best-effort cleanup of empty check created before attach failed
        }
      }
      try {
        await http.patch(`/api/orders/${order._id}/status`, {
          status: 'cancelled',
          returnItemIds: items.map((i) => i._id),
        });
      } catch (cancelErr) {
        const err = new Error(
          'Order was created but check setup failed and the order could not be cancelled. '
          + 'Please cancel the order manually.',
        );
        err.orderId = order._id;
        err.cause = checkErr;
        err.cancelError = cancelErr;
        throw err;
      }
      throw checkErr;
    }
  }

  return { order, items, checkId, itemErrors };
}
